import api, { setTokens, clearTokens } from './client';
import { STORAGE_KEYS } from './config';
import type {
  User,
  AuthTokens,
  RefreshTokenResponse,
  LoginRequest,
  RegisterRequest,
  RefreshTokenRequest,
} from './types';

export const authService = {
  /**
   * Register a new user
   */
  register: async (data: RegisterRequest): Promise<AuthTokens> => {
    const response = await api.post<AuthTokens>('/auth/register', data, { skipAuth: true });

    // Store tokens and user
    setTokens(response.accessToken, response.refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.user));

    return response;
  },

  /**
   * Login user
   */
  login: async (data: LoginRequest): Promise<AuthTokens> => {
    const response = await api.post<AuthTokens>('/auth/login', data, { skipAuth: true });

    // Store tokens and user
    setTokens(response.accessToken, response.refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.user));

    return response;
  },

  /**
   * Login or register with Google ID token
   */
  loginWithGoogle: async (idToken: string): Promise<AuthTokens> => {
    const response = await api.post<AuthTokens>('/auth/google', { idToken }, { skipAuth: true });

    // Store tokens and user
    setTokens(response.accessToken, response.refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.user));

    return response;
  },

  loginWithMicrosoft: async (idToken: string): Promise<AuthTokens> => {
    const response = await api.post<AuthTokens>('/auth/microsoft', { idToken }, { skipAuth: true });
    setTokens(response.accessToken, response.refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.user));
    return response;
  },

  /**
   * Refresh access token
   */
  refreshToken: async (data: RefreshTokenRequest): Promise<RefreshTokenResponse> => {
    const response = await api.post<RefreshTokenResponse>('/auth/refresh', data, { skipAuth: true });

    // Store new tokens
    setTokens(response.accessToken, response.refreshToken);

    return response;
  },

  /**
   * Logout user
   */
  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Continue with local logout even if API call fails
      console.error('Logout API call failed:', error);
    } finally {
      clearTokens();
    }
  },

  /**
   * Get current user profile
   */
  getCurrentUser: async (): Promise<User> => {
    const user = await api.get<User>('/auth/me');

    // Update stored user
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));

    return user;
  },

  /**
   * Check if user is authenticated (has valid tokens)
   */
  isAuthenticated: (): boolean => {
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    return !!(accessToken && refreshToken);
  },

  /**
   * Get stored user from localStorage
   */
  getStoredUser: (): User | null => {
    const userJson = localStorage.getItem(STORAGE_KEYS.USER);
    if (!userJson) return null;

    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  },

  /**
   * Persist user to localStorage (single source of truth for the storage key)
   */
  storeUser: (user: User): void => {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  },
};

export default authService;